import { createFileRoute, Link, useNavigate } from "@tanstack/solid-router";
import { createEffect, createSignal, ErrorBoundary, Suspense } from "solid-js";
import { SourceCard } from "~/components/sources/SourceCard";
import { SourcesSkeleton } from "~/components/sources/SourcesSkeleton";
import {
  SourcesViewToggle,
  type ViewMode,
} from "~/components/sources/SourcesViewToggle";
import { SourceTable } from "~/components/sources/SourceTable";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { ErrorCard } from "~/components/ui/ErrorCard";
import { useProjectQuery } from "~/lib/queries/projects";
import { useSourcesQuery } from "~/lib/queries/sources";

export const Route = createFileRoute(
  "/_authenticated/projects/$projectId/sources/",
)({
  ssr: false,
  component: SourcesListPage,
});

function SourcesListContent() {
  const params = Route.useParams();
  const navigate = useNavigate();

  const [view, setView] = createSignal<ViewMode>("grid");
  const [page, setPage] = createSignal(1);
  const [tokens, setTokens] = createSignal<Record<number, string>>({ 1: "" });

  const projectQuery = useProjectQuery(() => params().projectId);

  const pageToken = () => tokens()[page()] ?? "";
  const sourcesQuery = useSourcesQuery(() => params().projectId, pageToken, 12);

  createEffect(() => {
    const data = sourcesQuery.data;
    if (data?.nextPageToken) {
      const nextPage = page() + 1;
      setTokens((prev) => {
        if (prev[nextPage] === data.nextPageToken) {
          return prev;
        }
        return { ...prev, [nextPage]: data.nextPageToken };
      });
    }
  });

  const projectName = () => projectQuery.data?.name || "Project";
  const sources = () => sourcesQuery.data?.sources ?? [];
  const hasNextPage = () => !!sourcesQuery.data?.nextPageToken;

  const handleSourceClick = (sourceId: string) => {
    navigate({
      to: "/projects/$projectId/sources/$sourceId",
      params: { projectId: params().projectId, sourceId },
    });
  };

  const handlePrevPage = () => {
    if (page() > 1) {
      setPage((p) => p - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage()) {
      setPage((p) => p + 1);
    }
  };

  return (
    <div class="space-y-6 max-w-6xl mx-auto">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          {
            label: projectName(),
            to: `/projects/${params().projectId}`,
          },
          { label: "Sources" },
        ]}
      />

      {/* Header */}
      <div class="flex items-center justify-between gap-4">
        <div>
          <h1 class="text-xl font-bold text-foreground">Sources</h1>
          <p class="text-sm text-muted-foreground mt-0.5">
            {sourcesQuery.isSuccess
              ? `Displaying sources page ${page()}`
              : "Loading sources…"}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <SourcesViewToggle view={view()} onChange={setView} />
          <Link
            to="/projects/$projectId/sources/new"
            params={{ projectId: params().projectId }}
            mask={{
              to: "/projects/$projectId/sources",
              params: { projectId: params().projectId },
              unmaskOnReload: true,
            }}
            class="inline-flex h-9 items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground
                   text-sm font-medium hover:bg-primary/90 transition-colors shrink-0
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span aria-hidden="true">+</span>
            New Source
          </Link>
        </div>
      </div>

      {/* Loading Skeleton */}
      {sourcesQuery.isPending && <SourcesSkeleton view={view()} />}

      {/* Error State */}
      {sourcesQuery.isError && (
        <ErrorCard
          message="Failed to load sources."
          action={
            <button
              type="button"
              onClick={() => sourcesQuery.refetch()}
              class="inline-flex h-8 items-center px-3 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          }
        />
      )}

      {/* Empty State */}
      {sourcesQuery.isSuccess && sources().length === 0 && (
        <div class="rounded-xl border border-dashed border-border p-16 text-center bg-card">
          <p class="text-sm text-muted-foreground mb-4">
            No sources in this project yet. Create your first archival source.
          </p>
          <Link
            to="/projects/$projectId/sources/new"
            params={{ projectId: params().projectId }}
            mask={{
              to: "/projects/$projectId/sources",
              params: { projectId: params().projectId },
              unmaskOnReload: true,
            }}
            class="inline-flex h-9 items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground
                   text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <span aria-hidden="true">+</span>
            New Source
          </Link>
        </div>
      )}

      {/* Grid View */}
      {sourcesQuery.isSuccess && sources().length > 0 && view() === "grid" && (
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources().map((source) => (
            <SourceCard
              projectId={params().projectId}
              source={source}
              onClick={() => handleSourceClick(source.id)}
            />
          ))}
        </div>
      )}

      {/* Table View */}
      {sourcesQuery.isSuccess && sources().length > 0 && view() === "table" && (
        <SourceTable
          projectId={params().projectId}
          sources={sources()}
          onSourceClick={handleSourceClick}
        />
      )}

      {/* Pagination Controls */}
      {sourcesQuery.isSuccess && (sources().length > 0 || page() > 1) && (
        <div class="flex items-center justify-between border-t border-border pt-4">
          <button
            type="button"
            onClick={handlePrevPage}
            disabled={page() === 1 || sourcesQuery.isPending}
            class="h-9 px-3 rounded-lg border border-border text-xs font-medium hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <span class="text-xs text-muted-foreground font-medium">
            Page {page()}
          </span>
          <button
            type="button"
            onClick={handleNextPage}
            disabled={!hasNextPage() || sourcesQuery.isPending}
            class="h-9 px-3 rounded-lg border border-border text-xs font-medium hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function SourcesListPage() {
  return (
    <ErrorBoundary
      fallback={(err) => (
        <ErrorCard
          title="Sources error"
          message={err?.message ?? "An unexpected error occurred."}
        />
      )}
    >
      <Suspense fallback={<SourcesSkeleton />}>
        <SourcesListContent />
      </Suspense>
    </ErrorBoundary>
  );
}
